import { useEffect, useRef, useState } from "react";
import { navLinks, implementsMenu, siteConfig } from "../../data/mock";
import { Menu, X, Terminal, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const ImplementsItem = ({ item, onNavigate }) => {
  const badge = item.isNew ? (
    <span className="nav-new-marker">NEW</span>
  ) : item.comingSoon ? (
    <span className="nav-soon-marker">SOON</span>
  ) : null;

  if (item.href) {
    return (
      <Link className="implements-item" to={item.href} onClick={onNavigate}>
        <span>{item.label}</span>
        {badge}
      </Link>
    );
  }

  return (
    <span
      className={`implements-item is-static${item.comingSoon ? " is-disabled" : ""}`}
      aria-disabled="true"
    >
      <span>{item.label}</span>
      {badge}
    </span>
  );
};

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [implementsOpen, setImplementsOpen] = useState(false);
  const implementsRef = useRef(null);
  const location = useLocation();

  const isActive = (href) => location.pathname === href;
  const implementsActive = implementsMenu.some(
    (item) => item.href && location.pathname === item.href
  );

  // Close the dropdown on outside click or route change.
  useEffect(() => {
    const handleClick = (event) => {
      if (implementsRef.current && !implementsRef.current.contains(event.target)) {
        setImplementsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setImplementsOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header className="site-nav">
      <Link to="/" className="brand-button" onClick={() => setMobileOpen(false)}>
        <span className="brand-mark" aria-hidden="true">
          <Terminal size={16} />
        </span>
        <span className="brand-wordmark">
          {siteConfig.name}
          <span>.ai</span>
        </span>
      </Link>

      <nav aria-label="Primary navigation">
        {navLinks.map((link) => (
          <Link
            key={link.label}
            to={link.href}
            className={isActive(link.href) ? "nav-link active" : "nav-link"}
          >
            <span>{link.label}</span>
            {link.isNew ? <span className="nav-new-marker">NEW</span> : null}
          </Link>
        ))}

        <div className="implements-menu" ref={implementsRef}>
          <button
            type="button"
            className={`nav-link${implementsActive || implementsOpen ? " active" : ""}`}
            aria-haspopup="true"
            aria-expanded={implementsOpen}
            onClick={() => setImplementsOpen((open) => !open)}
          >
            <span>Lab0 implements</span>
            <ChevronDown
              size={14}
              aria-hidden="true"
              className={`implements-caret${implementsOpen ? " is-open" : ""}`}
            />
          </button>

          {implementsOpen && (
            <div className="implements-dropdown" role="menu">
              {implementsMenu.map((item) => (
                <ImplementsItem
                  key={item.label}
                  item={item}
                  onNavigate={() => setImplementsOpen(false)}
                />
              ))}
            </div>
          )}
        </div>

        <a className="nav-link demo-nav" href={siteConfig.demoUrl} target="_blank" rel="noreferrer">
          Book demo
        </a>
      </nav>

      <button
        className="mobile-menu-button"
        type="button"
        aria-label="Open navigation"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {mobileOpen && (
        <div className="mobile-nav-panel">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              onClick={() => setMobileOpen(false)}
              className={isActive(link.href) ? "nav-link active" : "nav-link"}
            >
              <span>{link.label}</span>
              {link.isNew ? <span className="nav-new-marker">NEW</span> : null}
            </Link>
          ))}

          <div className="mobile-implements">
            <span className="mobile-implements-label">Lab0 implements</span>
            {implementsMenu.map((item) => (
              <ImplementsItem
                key={item.label}
                item={item}
                onNavigate={() => setMobileOpen(false)}
              />
            ))}
          </div>

          <a
            className="nav-link demo-nav"
            href={siteConfig.demoUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => setMobileOpen(false)}
          >
            Book demo
          </a>
        </div>
      )}
    </header>
  );
};
